import _ from "lodash";
import { FormattingHelpers } from "@ai360/core";
import moment from "moment";
import { adminData, GUID, NAME } from "~/admin/data";
import { ISelectOption } from "@ai360/core";
import { IS_SELECTABLE } from "~/utils/products/use-product-list";

export const getAgBytesErrorClassNames = (
    errorCodes: number | number[],
    apiErrors,
    classNames = [],
    applyClass = "form-input-error"
) => {
    const retClassNames = [...classNames];
    errorCodes = Array.isArray(errorCodes) ? errorCodes : [errorCodes];
    errorCodes.some((errorCode) => {
        if (apiErrors && apiErrors.includes(errorCode)) {
            retClassNames.push(applyClass);
            return true;
        }
        return false;
    });
    return retClassNames;
};

export const prepareSelectableOptions = (
    options,
    { guid, label, id = undefined, appendIdToLabel = false, details = null }
): ISelectOption<any>[] =>
    _.reduce(
        options,
        (result, option) => {
            let labelText = option[label];
            if (appendIdToLabel && id) {
                labelText = `${option[id]} - ${labelText}`;
            }
            result.push({
                value: {
                    guid: option[guid],
                    id: option[id],
                    name: option[label],
                    activeYn:
                        option[adminData.PROPS_ACTIVE_YN] !== undefined
                            ? option[adminData.PROPS_ACTIVE_YN]
                            : true,
                    details: option[details],
                    // Only present when the source set it (e.g. RestrictedProductList); must be absent, not
                    // undefined, elsewhere — SelectInput matches values with lodash isEqual, and a
                    // present-but-undefined key breaks matching against values rebuilt by mapToPicklistValue.
                    ...(option[IS_SELECTABLE] !== undefined && {
                        isSelectable: option[IS_SELECTABLE],
                    }),
                    // Same carry-through convention as isSelectable — absent unless the source set it (e.g.
                    // toSelectableCropDropdownOptions), so callers needing "is the selected value itself
                    // unlinked" can read it off mapToPicklistValue's result.
                    ...(option.isAgvanceLinked !== undefined && {
                        isAgvanceLinked: option.isAgvanceLinked,
                    }),
                },
                label: labelText,
                // Only present when the source set it (e.g. RestrictedCropList), same convention as
                // isSelectable. Lands at the top level, sibling to value/label — SelectInput's option
                // renderer reads option.getToolTip directly, and since mapToPicklistValue only rebuilds
                // `value`, this key can't collide with its isEqual-based matching.
                ...(option.getToolTip !== undefined && { getToolTip: option.getToolTip }),
            });
            return result;
        },
        []
    );

// Wraps a raw-options-to-select-options transform (e.g. prepareSelectableOptions) so it only
// recomputes when the raw input array reference actually changes. Meant to replace the
// componentDidUpdate/UNSAFE_componentWillReceiveProps "copy props into state" pattern seen across
// admin add/edit panels -- deriving state from props there is inherently fragile to render/update
// timing (see HW-12709's Bug 4/6/7 family). Compute options directly in render with this instead.
export const memoizeSelectableOptions = <T>(
    transform: (rawOptions: T) => ISelectOption<any>[]
): ((rawOptions: T) => ISelectOption<any>[]) => {
    let lastInput: T;
    let lastOutput: ISelectOption<any>[] = [];
    let hasRun = false;
    return (rawOptions: T): ISelectOption<any>[] => {
        if (!hasRun || rawOptions !== lastInput) {
            hasRun = true;
            lastInput = rawOptions;
            lastOutput = transform(rawOptions);
        }
        return lastOutput;
    };
};

// Same shape as memoizeSelectableOptions, for the AgBytes/Setup "Log History" modals'
// formatLogDetailText call. Those files used to mutate each record's detailsMessage in place from
// componentDidUpdate -- a mutation with no setState, invisible to DataTable's own React.memo (which
// bails out on `records` reference equality), so the formatted text painted only by accident, on
// whatever unrelated re-render happened to land after the mutation but before the next raw fetch
// overwrote it. Formatting a copy here, in render, guarantees the DataTable's own render sees the
// formatted text on the same pass that introduces the new `records` reference.
export const memoizeFormattedRecords = <T>(
    formatRecord: (record: T) => T
): ((records?: T[]) => T[] | undefined) => {
    let lastInput: T[] | undefined;
    let lastOutput: T[] | undefined;
    let hasRun = false;
    return (records?: T[]): T[] | undefined => {
        if (!hasRun || records !== lastInput) {
            hasRun = true;
            lastInput = records;
            lastOutput = records?.map((record) => formatRecord({ ...record }));
        }
        return lastOutput;
    };
};

export const mapToPicklistValue = ({ options = [], selectedGuid = "" }) => {
    if (options.length > 0 && selectedGuid) {
        const selectedOption = options.filter((option) => {
            return selectedGuid === option.value[GUID];
        });
        const { id, name, guid, activeYn, details, isSelectable, isAgvanceLinked } =
            selectedOption.length > 0 && selectedOption[0].value;
        return {
            activeYn,
            guid,
            id,
            name,
            details,
            ...(isSelectable !== undefined && { isSelectable }),
            ...(isAgvanceLinked !== undefined && { isAgvanceLinked }),
        };
    }
    return null;
};

export const mapNameToPicklistValue = ({ options = [], selectedName = "" }) => {
    if (options.length > 0 && selectedName) {
        const selectedOption = options.filter((option) => {
            return selectedName === option.value[NAME];
        });
        const { id, name, guid, activeYn, details, isSelectable } =
            selectedOption.length > 0 && selectedOption[0].value;
        return {
            activeYn,
            guid,
            id,
            name,
            details,
            // See mapToPicklistValue -- same isEqual-matching requirement.
            ...(isSelectable !== undefined && { isSelectable }),
        };
    }
    return null;
};

export const handlePicklistChange = (record, { type, guid, value }, callback, newApi = false) => {
    if (!value) {
        record[type] = "";
        record[guid] = !newApi ? "" : null;
    } else if (guid) {
        if (type) {
            record[type] = value.name;
        }
        record[guid] = value.guid;
    } else {
        console.debug(`${type} / ${guid} / ${JSON.stringify(value)} value is null`);
    }
    callback && callback(value || {});
    return record;
};

export const onNumberChange = (record, { formKey, value }, callback?: () => void) =>
    onTextChange(
        record,
        {
            formKey,
            value: value === "" ? value : Number(FormattingHelpers.parseStringToInt(value)),
        },
        callback
    );

export const onTextChange = (record, { formKey, value }, callback) => {
    if (formKey && value != null) {
        record[formKey] = value;
        callback && callback();
    }
    return record;
};

export const onTextChangeObj = (record, { formKey, propertyName, value }, callback) => {
    if (formKey && value != null) {
        record[formKey][propertyName] = value;
        callback && callback();
    } else {
        console.error(`${formKey}, ${propertyName} or ${value} value is null`);
    }
    return record;
};

export const parseLabel = (label) => {
    if (label) {
        const arr = label.trim().split("-");
        return { id: arr[0], name: arr[1] };
    }
};

export const getCountryGuid = ({ options = [], selectedId = "3" }) => {
    if (options.length) {
        const selectedOption = options.filter((option) => {
            return selectedId === option.value.id;
        });
        const { guid } = selectedOption.length && selectedOption[0].value;
        return guid;
    }
    return null;
};

// parse moment object to date string
export const formatDate = (dateVal, format = "M/D/YYYY, hh:mm:ss A") => {
    if (dateVal) {
        return moment(dateVal).format(format);
    }
    return null;
};

export const getLabel = ({ options = [], guid }, valueKey = "guid", labelKey = "label") => {
    if (options.length) {
        const selectedOption = options.filter((option) => {
            return guid === option.value[valueKey];
        });
        return selectedOption.length ? selectedOption[0][labelKey] : null;
    }
    return null;
};

export const getGuid = ({ options = [], label }, valueKey = "guid", labelKey = "label") => {
    if (options.length) {
        const selectedOption = options.filter((option) => {
            return label === option[labelKey];
        });
        return selectedOption.length ? selectedOption[0].value[valueKey] : null;
    }
    return null;
};

/**
 * Concatenates the passed i18n message and the value into the proper format for a plain text label
 * @param {formatMessage} function passed for i18n
 * @param {label} the i18n object to be formatted
 * @param {value} the string value to be formatted
 */
export function formatPlainTextLabel(formatMessage, label, value) {
    return `${formatMessage(label)}: ${value}`;
}

export const onTextChangeMultiProp = (record, { parentKey, formKey, value }, callback) => {
    if (parentKey && formKey && value != null) {
        record[parentKey][formKey] = value;
        callback && callback();
    } else {
        console.error(`${parentKey}, ${formKey} or ${value} value is null`);
    }
    return record;
};

/**
 * Prevents events from bubbling up when submitting a form
 * @param {event}
 */
export const preventBubbleUp = (event) => {
    event.preventDefault();
    event.stopPropagation();
};

/**
 * Prevents events from bubbling up when submitting a form
 * @param {arr} Array to be mapped and add restrictAddEdit property to it
 */
export const addRestrictEditDeleteModeZtoN = (arr) => {
    return arr.map((item) => ({ ...item, restrictEditDelete: true }));
};

export const hasOnlyInActiveOptions = (
    options = [],
    valueKey = "value",
    allowEmptyOptions = false
) => {
    if (!options || !options.length) {
        return !allowEmptyOptions;
    } else {
        const isUndefined = (value) => value === undefined;
        return !options.some((option) => {
            if (
                typeof option[valueKey] === "object" &&
                option[valueKey] &&
                adminData.PROPS_ACTIVE_YN in option[valueKey]
            ) {
                const activeYn = option[valueKey][adminData.PROPS_ACTIVE_YN];
                return activeYn || isUndefined(activeYn);
            } else if (adminData.PROPS_ACTIVE_YN in option) {
                const activeYn = option[adminData.PROPS_ACTIVE_YN];
                return activeYn || isUndefined(activeYn);
            } else {
                return option;
            }
        });
    }
};

export const toCamelCase = (attr: string) => {
    return `${attr[0].toLowerCase()}${attr.slice(1)}`;
};
